// GS1 element-string parsing — chapter 3 §3.2 (R3.1), ADR-0002, D3-2. All parsing and
// validation is delegated to the GS1 Barcode Syntax Engine (gs1encoder, Apache-2.0);
// this module only normalises input forms and types the result.
//
// Accepted inputs (parseScan):
//   - scanner output with AIM symbology identifier: "]C1010735…" with ASCII 29 (GS)
//     between variable-length fields, "]d2…", "]Q3…", "]E0…" (EAN/UPC), "]Q1https://…"
//   - a GS1 Digital Link URI ("https://…/01/…")
//   - human-friendly AI syntax "(01)…(10)…"
//   - raw GS1 data with "^" or GS as FNC1, or a bare 8/12/13/14-digit GTIN
import { GS1encoder } from "gs1encoder";
export const GS = "\u001d"; // ASCII group separator, FNC1 in scanner output
export class GS1ParseError extends Error {
    input;
    markup;
    constructor(message, input, markup) {
        super(message);
        this.input = input;
        this.markup = markup;
        this.name = "GS1ParseError";
    }
}
let enginePromise = null;
/** The engine instance is shared; the WASM module loads once per process. */
export async function gs1Engine() {
    enginePromise ??= GS1encoder.create().then((gs) => {
        gs.permitUnknownAIs = false;
        gs.includeDataTitlesInHRI = false;
        gs.validateAIassociations = true;
        return gs;
    });
    return enginePromise;
}
const HRI_LINE = /^\((\d{2,4})\)\s?(.*)$/;
function hriToMap(hri) {
    const out = {};
    for (const line of hri) {
        if (line === "--")
            continue;
        const m = HRI_LINE.exec(line);
        if (m)
            out[m[1]] = m[2] ?? "";
    }
    return out;
}
/**
 * GS1 date (YYMMDD; DD may be "00" meaning end of month) → ISO date. Century per GS1
 * General Specifications §7.12: within ±50 years of the current year.
 */
export function gs1DateToIso(yymmdd, now = new Date()) {
    if (!/^\d{6}$/.test(yymmdd))
        throw new GS1ParseError(`invalid GS1 date ${yymmdd}`, yymmdd);
    const yy = Number(yymmdd.slice(0, 2));
    const mm = Number(yymmdd.slice(2, 4));
    let dd = Number(yymmdd.slice(4, 6));
    const currentYear = now.getUTCFullYear();
    const currentCentury = Math.floor(currentYear / 100) * 100;
    let year = currentCentury + yy;
    const diff = year - currentYear;
    if (diff > 50)
        year -= 100;
    else if (diff < -50)
        year += 100;
    if (mm < 1 || mm > 12)
        throw new GS1ParseError(`invalid month in GS1 date ${yymmdd}`, yymmdd);
    if (dd === 0)
        dd = new Date(Date.UTC(year, mm, 0)).getUTCDate(); // last day of month
    const d = new Date(Date.UTC(year, mm - 1, dd));
    if (d.getUTCMonth() !== mm - 1 || d.getUTCDate() !== dd) {
        throw new GS1ParseError(`invalid day in GS1 date ${yymmdd}`, yymmdd);
    }
    return d.toISOString().slice(0, 10);
}
function decimalAi(ai, prefix) {
    // 310n / 311n: six digits with n implied decimal places.
    for (let n = 0; n <= 5; n++) {
        const v = ai[`${prefix}${n}`];
        if (v !== undefined)
            return Number(v) / 10 ** n;
    }
    return null;
}
function typed(ai, symbologyId, digitalLink, hri) {
    const date = (code) => (ai[code] ? gs1DateToIso(ai[code]) : null);
    const count = ai["30"] ?? ai["37"];
    return {
        ai,
        symbologyId,
        digitalLink,
        gtin: ai["01"] ?? null,
        containedGtin: ai["02"] ?? null,
        sscc: ai["00"] ?? null,
        lot: ai["10"] ?? null,
        serial: ai["21"] ?? null,
        productionDate: date("11"),
        packagingDate: date("13"),
        bestBefore: date("15"),
        expiry: date("17"),
        count: count !== undefined ? Number(count) : null,
        netWeightKg: decimalAi(ai, "310"),
        lengthM: decimalAi(ai, "311"),
        hri,
    };
}
const AIM_PREFIX = /^\](\w)(\w)/;
/** Normalise any accepted input form and parse it with the engine. */
export async function parseScan(input) {
    const gs = await gs1Engine();
    const trimmed = input.trim();
    const aim = AIM_PREFIX.exec(trimmed);
    const symbologyId = aim ? trimmed.slice(0, 3) : null;
    try {
        // The engine instance is shared and stateful: reset the symbology so a previous
        // EAN/UPC scan does not constrain how the next input is interpreted.
        gs.sym = GS1encoder.symbology.NONE;
        // Scanning accepts *partial* element strings: a label may split its data over
        // several codes (e.g. "(15)(10)" beside "(01)(3110)"), which scan assembly merges
        // later (spec §3.3). The requisite-AI rule (AI 10 requires 01/02) is therefore
        // enforced when encoding labels, not when reading them; mutual-exclusion and
        // repeated-AI checks stay on.
        gs.setValidationEnabled(GS1encoder.validation.RequisiteAIs, false);
        if (aim) {
            gs.scanData = trimmed;
        }
        else if (/^https?:\/\//i.test(trimmed)) {
            gs.dataStr = trimmed;
        }
        else if (trimmed.startsWith("(")) {
            gs.aiDataStr = trimmed;
        }
        else if (trimmed.startsWith("^") || trimmed.includes(GS)) {
            const raw = trimmed.startsWith("^") ? trimmed : `^${trimmed}`;
            gs.dataStr = raw.replaceAll(GS, "^");
        }
        else if (/^\d{8}$|^\d{12,14}$/.test(trimmed)) {
            gs.aiDataStr = `(01)${trimmed.padStart(14, "0")}`;
        }
        else {
            // Element string without FNC1 markers (e.g. keyboard wedge that dropped GS):
            // only parseable when every AI is fixed-length; let the engine decide.
            gs.dataStr = `^${trimmed}`;
        }
    }
    catch (err) {
        const markup = safeMarkup(gs);
        throw new GS1ParseError(err instanceof Error ? err.message : String(err), input, markup);
    }
    let hri = gs.hri;
    let ai = hriToMap(hri);
    if (Object.keys(ai).length === 0 && isEanUpc(gs.sym)) {
        // EAN/UPC symbols carry a bare GTIN with no AI; present it as AI 01 (GTIN-14).
        const digits = gs.dataStr.replace(/\D/g, "");
        const gtin = digits.padStart(14, "0");
        ai = { "01": gtin };
        hri = [`(01) ${gtin}`];
    }
    const digitalLink = /^https?:\/\//i.test(trimmed) || /^\](Q1|d1)/.test(trimmed);
    return typed(ai, symbologyId, digitalLink, hri);
}
function isEanUpc(sym) {
    const s = GS1encoder.symbology;
    return sym === s.EAN13 || sym === s.EAN8 || sym === s.UPCA || sym === s.UPCE;
}
function safeMarkup(gs) {
    try {
        return gs.errMarkup || undefined;
    }
    catch {
        return undefined;
    }
}
/** Human-friendly AI syntax from a map of AIs, e.g. for label templates. */
export function toAiDataStr(ai) {
    return Object.entries(ai)
        .map(([k, v]) => `(${k})${v.replaceAll("(", "\\(")}`)
        .join("");
}
/** Validate and normalise an AI map through the engine; returns the canonical element string with "^" as FNC1. */
export async function toElementString(ai) {
    const gs = await gs1Engine();
    try {
        gs.sym = GS1encoder.symbology.NONE;
        gs.setValidationEnabled(GS1encoder.validation.RequisiteAIs, true); // labels we print must be complete
        gs.aiDataStr = toAiDataStr(ai);
    }
    catch (err) {
        throw new GS1ParseError(err instanceof Error ? err.message : String(err), toAiDataStr(ai), safeMarkup(gs));
    }
    return gs.dataStr;
}
/** GS1 Digital Link URI for an AI map, with the tenant's resolver stem (or GS1's canonical one). */
export async function toDigitalLink(ai, stem) {
    const gs = await gs1Engine();
    gs.sym = GS1encoder.symbology.NONE;
    gs.setValidationEnabled(GS1encoder.validation.RequisiteAIs, true);
    gs.aiDataStr = toAiDataStr(ai);
    return gs.getDLuri(stem ?? null);
}
//# sourceMappingURL=gs1.js.map